/**
 * Tags every mutating API call with where it came from, so the Daily Ops
 * Stats admin page (team.influencer.in/admin/team/dailyopsdashboard/) can
 * show "Planner › Unlock profile" instead of a raw endpoint.
 *
 * The tag travels as a `_src=<page>|<action>` query parameter (read by
 * team.middleware.DailyOpsSourceMiddleware). A query string — unlike a
 * custom header — never turns the request into a CORS-preflighted one,
 * so this needs no server-side whitelist and no deploy ordering.
 *
 *   page   — window.location.pathname, always.
 *   action — the button / flow, passed per call via opsUrl(). Deliberately
 *            a per-call argument and not ambient state: a module-level
 *            "current action" would mislabel any other request that
 *            happened to be in flight at the same time.
 *
 * Only POST/PUT/PATCH/DELETE to API_HOST are tagged: those create the
 * records the dashboard attributes; GETs and other hosts are untouched.
 * install() patches window.fetch and the default axios instance; every
 * axios.create() instance must also be passed to tagAxios() (api_handlers).
 */
import axios from 'axios';
import { API_HOST } from '../config';

const PARAM = '_src';
const MUTATING = ['POST', 'PUT', 'PATCH', 'DELETE'];
function sourceTag(action) {
  const page = window.location.pathname;
  return action ? `${page}|${String(action).slice(0, 120)}` : page;
}

function withTag(url, action) {
  if (!url || url.includes(`${PARAM}=`)) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}${PARAM}=${encodeURIComponent(sourceTag(action))}`;
}

function shouldTag(url, method) {
  if (!url || typeof url !== 'string' || !url.startsWith(API_HOST)) return false;
  return MUTATING.includes(String(method || 'GET').toUpperCase());
}

/** URL with the source tag appended — for hand-written fetch/axios calls. */
export function opsUrl(url, action) {
  return withTag(url, action);
}

export function tagAxios(instance) {
  instance.interceptors.request.use((config) => {
    const full = config.baseURL ? `${config.baseURL}${config.url || ''}` : config.url;
    if (shouldTag(full, config.method)) {
      config.url = withTag(config.url);
    }
    return config;
  });
  return instance;
}

let installed = false;
export function install() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  tagAxios(axios);
  const nativeFetch = window.fetch.bind(window);
  window.fetch = (input, init) => {
    const method = (init && init.method) || 'GET';
    if (!shouldTag(input, method)) return nativeFetch(input, init);
    return nativeFetch(withTag(input), init);
  };
}
